import {
  type ApprovalCloseReason,
  type ApprovalDecisionAuthority,
  type ApprovalRequestStatus,
  OrderAction,
  type OrderStatus,
} from "@prisma/client";
import { z } from "zod";
import { targetedShipmentForwardCommandSchema } from "~/shared/orders/targeted-shipment";
import { type EffectLocationToken } from "~/shared/types/EffectLocation";

import { type OrderApprovalAction } from "./approval-action-payload";
import { type OrderApprovalReasonSnapshotV1 } from "./approval-reason-snapshot";
import { OrderActionSubmitSchema, OrderActivityMarkDeliveredSchema, OrderActivityShipInputSchema, OrderActivityShipSchema } from "./schema";

// Keep the owner's validation, including cross-field date checks, at the request boundary.
const actionSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("TARGETED"), input: targetedShipmentForwardCommandSchema }).strict(),
  z
    .object({
      action: z.literal(OrderAction.SUBMIT),
      input: OrderActionSubmitSchema.extend({ action: z.literal(OrderAction.SUBMIT) }).strict(),
    })
    .strict(),
  z
    .object({
      action: z.literal(OrderAction.SHIP),
      input: OrderActivityShipInputSchema.extend({ action: z.literal(OrderAction.SHIP) })
        .strict()
        .pipe(OrderActivityShipSchema)
        .and(z.object({ action: z.literal(OrderAction.SHIP) })),
    })
    .strict(),
  z
    .object({
      action: z.literal(OrderAction.MARK_DELIVERED),
      input: OrderActivityMarkDeliveredSchema.innerType()
        .extend({ action: z.literal(OrderAction.MARK_DELIVERED) })
        .strict()
        .pipe(OrderActivityMarkDeliveredSchema)
        .and(z.object({ action: z.literal(OrderAction.MARK_DELIVERED) })),
    })
    .strict(),
]);

const newPreviewSchema = z.object({ intent: z.literal("new"), action: actionSchema }).strict();

export const orderApprovalPreviewInputSchema = newPreviewSchema;
export type OrderApprovalPreviewInput = z.infer<typeof orderApprovalPreviewInputSchema>;

export const MAX_APPROVAL_NOTE_LENGTH = 2_000;

const requestFields = { note: z.string().trim().max(MAX_APPROVAL_NOTE_LENGTH).nullable().optional() };
export const orderApprovalRequestInputSchema = newPreviewSchema.extend(requestFields).strict();
export type OrderApprovalRequestInput = z.infer<typeof orderApprovalRequestInputSchema>;

export const orderApprovalReadInputSchema = z.object({ orderId: z.string().uuid() }).strict();
export type OrderApprovalReadInput = z.infer<typeof orderApprovalReadInputSchema>;

export const orderApprovalDecisionInputSchema = z.discriminatedUnion("decision", [
  z
    .object({
      requestId: z.string().uuid(),
      decision: z.literal("approve"),
      note: z.string().trim().max(MAX_APPROVAL_NOTE_LENGTH).optional(),
    })
    .strict(),
  z
    .object({ requestId: z.string().uuid(), decision: z.literal("reject"), note: z.string().trim().min(1).max(MAX_APPROVAL_NOTE_LENGTH) })
    .strict(),
]);
export type OrderApprovalDecisionInput = z.infer<typeof orderApprovalDecisionInputSchema>;
export const orderApprovalRescindInputSchema = z.object({ requestId: z.string().uuid() }).strict();

export type OrderApprovalActionOutcome =
  | { readonly kind: "not_attempted" }
  | { readonly kind: "advanced"; readonly status: OrderStatus; readonly message?: string }
  | { readonly kind: "blocked" | "unknown"; readonly message: string };

export interface OrderApprovalDecisionResult {
  readonly requestId: string;
  readonly orderId: string;
  readonly status: ApprovalRequestStatus;
  readonly changed: boolean;
  readonly actionOutcome: OrderApprovalActionOutcome;
}

export type OrderApprovalCapabilityKey = "canDecide" | "canRescind";
export type OrderApprovalCapabilities = Readonly<Record<OrderApprovalCapabilityKey, boolean>> & {
  readonly reasons: Readonly<Record<OrderApprovalCapabilityKey, string | null>>;
};

export interface OrderApprovalFinding {
  readonly ruleId: string;
  readonly ruleName: string;
  readonly message: string | null;
}

export interface OrderApprovalRequirement {
  readonly ruleId: string;
  readonly ruleRevision: number;
  readonly ruleLabel: string;
  readonly message: string | null;
  readonly approverUserIds: readonly string[];
  readonly reasonSnapshot: OrderApprovalReasonSnapshotV1;
  readonly requestId: string | null;
  readonly status: ApprovalRequestStatus | null;
  readonly covered: boolean;
}

export interface OrderApprovalPreview {
  readonly orderId: string;
  readonly action: OrderApprovalAction;
  readonly fromStatus: OrderStatus;
  readonly blocks: readonly OrderApprovalFinding[];
  readonly warnings: readonly OrderApprovalFinding[];
  readonly requirements: readonly OrderApprovalRequirement[];
  readonly uncoveredRuleIds: readonly string[];
  readonly canRequestNew: boolean;
  readonly requestUnavailableReason: string | null;
}

export type OrderApprovalRequestResult =
  | {
      readonly kind: "created_or_reused";
      readonly preview: OrderApprovalPreview;
      readonly createdRequestIds: readonly string[];
      readonly reusedRequestIds: readonly string[];
    }
  | { readonly kind: "nothing_uncovered"; readonly preview: OrderApprovalPreview };

/** Explicit transport projection; live authority never comes from these immutable display fields. */
export interface OrderApprovalRequestHistory {
  readonly id: string;
  readonly action: OrderApprovalAction;
  readonly fulfillmentMode?: "TARGETED";
  readonly effectLocation: EffectLocationToken;
  readonly ruleId: string | null;
  readonly originalRuleId: string;
  readonly ruleRevision: number;
  readonly ruleLabel: string;
  readonly message: string | null;
  readonly reasonSnapshot: OrderApprovalReasonSnapshotV1;
  readonly approverUserIdsSnapshot: readonly string[];
  readonly requestedById: string;
  readonly requestedAt: Date;
  readonly requestNote: string | null;
  readonly status: ApprovalRequestStatus;
  readonly decidedById: string | null;
  readonly decidedAt: Date | null;
  readonly decisionNote: string | null;
  readonly decidedAs: ApprovalDecisionAuthority | null;
  readonly closedById: string | null;
  readonly closedAt: Date | null;
  readonly closeReason: ApprovalCloseReason | null;
  /** Authorization was spent on one action attempt; this does not assert the Order action succeeded. */
  readonly usedAt: Date | null;
  readonly usedById: string | null;
  readonly currentRuleValid: boolean;
  readonly capabilities: OrderApprovalCapabilities;
}

export interface OrderApprovalDetail {
  readonly orderId: string;
  readonly requests: readonly OrderApprovalRequestHistory[];
}
